import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, GBP, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Download, ShieldCheck, ArrowLeft, BellRing, Check, Flame } from "lucide-react";
import { Link } from "react-router-dom";

const inputCls = "w-full px-4 py-3 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function PromoVideo({ url }) {
  if (!url) return null;
  const embed = embedUrl(url);
  return (
    <div className="aspect-video w-full border border-[var(--line)] bg-black mb-6" data-testid="store-promo-video">
      {embed
        ? <iframe src={embed} title="Promo video" className="w-full h-full" allow="accelerometer; encrypted-media" allowFullScreen />
        : <video src={url} controls className="w-full h-full" />}
    </div>
  );
}

export default function StoreProduct() {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  useEffect(() => {
    api.get(`/store/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (user) { setName(user.name || ""); setEmail(user.email || ""); }
  }, [user]);

  const buy = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/store/checkout", {
        product_id: id, origin_url: window.location.origin,
        buyer_name: name.trim(), buyer_email: email.trim(),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  const notifyMe = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setNotifyLoading(true);
    try {
      await api.post(`/store/products/${id}/notify`, { name: name.trim(), email: email.trim() });
      setNotifyDone(true);
      toast.success("You're on the list — we'll email you the moment it's available.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add you to the list.");
    } finally { setNotifyLoading(false); }
  };

  if (notFound) return <div className="py-32 text-center text-[var(--ink-2)]" data-testid="store-product-missing">This product is no longer available. <Link to="/store" className="text-[var(--accent)] font-semibold">Back to store</Link></div>;
  if (!product) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10" data-testid="store-product-page">
      <Link to="/store" className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-2)] hover:text-[var(--ink)] mb-6"><ArrowLeft size={15} /> Back to store</Link>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="aspect-[3/4] bg-[var(--alt)] border border-[var(--line)] overflow-hidden flex items-center justify-center">
          {product.cover_url
            ? <img src={product.cover_url} alt={product.title} className="w-full h-full object-cover" data-testid="store-product-cover" />
            : <FileText className="text-[var(--ink-2)]" size={64} />}
        </div>
        <div>
          <span className="text-xs uppercase tracking-wide text-[var(--ink-2)] font-semibold">{product.category}</span>
          {product.popular && !product.coming_soon && (
            <span className="inline-flex items-center gap-1 ml-2 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]" data-testid="store-product-popular"><Flame size={12} /> Most popular</span>
          )}
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-1 mb-3" data-testid="store-product-title">{product.title}</h1>
          <div className="font-display font-extrabold text-2xl mb-4" data-testid="store-product-price">{GBP(product.price)}</div>
          <PromoVideo url={product.promo_video} />
          {product.description && <p className="text-[var(--ink-2)] whitespace-pre-line mb-6 leading-relaxed">{product.description}</p>}

          {product.coming_soon ? (
            notifyDone ? (
              <div className="border border-[var(--line)] bg-[var(--alt)] p-5 flex items-start gap-3" data-testid="store-notify-done">
                <Check className="text-[#1B5E20] shrink-0 mt-0.5" size={20} />
                <div><p className="text-sm font-semibold">You're on the waiting list</p><p className="text-sm text-[var(--ink-2)]">We'll email <strong>{email}</strong> the moment this title goes live.</p></div>
              </div>
            ) : (
              <form onSubmit={notifyMe} className="space-y-3 border border-[var(--line)] bg-white p-5" data-testid="store-notify-form">
                <p className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2"><BellRing size={15} /> Coming soon</p>
                <p className="text-sm text-[var(--ink-2)]">This title isn't available to download just yet. Leave your email and we'll let you know the minute it's ready.</p>
                <input data-testid="notify-name" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                <input data-testid="notify-email" required type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                <button disabled={notifyLoading} data-testid="notify-submit" className="w-full bg-[var(--ink)] text-white font-semibold py-3.5 hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <BellRing size={18} /> {notifyLoading ? "Adding you…" : "Notify me when available"}
                </button>
              </form>
            )
          ) : (
          <form onSubmit={buy} className="space-y-3 border border-[var(--line)] bg-white p-5" data-testid="store-buy-form">
            <p className="text-sm font-semibold">Get instant access</p>
            <input data-testid="buy-name" required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            <input data-testid="buy-email" required type="email" placeholder="Email (your download link is sent here)" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <button disabled={loading} data-testid="buy-now" className="w-full bg-[var(--accent)] text-white font-semibold py-3.5 hover:bg-[var(--accent-hover)] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <Download size={18} /> {loading ? "Redirecting to payment…" : `Buy now · ${GBP(product.price)}`}
            </button>
            <p className="text-xs text-[var(--ink-2)] flex items-center gap-1.5"><ShieldCheck size={14} /> Secure payment via Stripe. Download link valid for 7 days.</p>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
